import { forwardRef, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDebounce } from "react-use";

import { logger } from "c7-logger";

import {
  AutocompleteOptionType,
  DropdownAutocomplete,
  DropdownAutocompleteProps,
  PoweredByType,
} from "chameleon/lib/components";

import googleLogo from "checkout-experience/components/google-places-autocomplete/assets/google_on_white.png";
import { usePlacesService } from "checkout-experience/components/google-places-autocomplete/hooks/use-places-service";
import { mapAddressData } from "checkout-experience/components/google-places-autocomplete/map-address-data";
import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import {
  getValidatorId,
  runValidationRulesAutocomplete,
} from "checkout-experience/utils/validators/validation-rule-runner";
import { getValidatorsOrb } from "checkout-experience/utils/validators/validatorsOrb";
import { getValidatorsUpg } from "checkout-experience/utils/validators/validatorsUpg";

import { PaymentGatewayType } from "frontend-client";

type GooglePlacesAutocompleteProps = DropdownAutocompleteProps;

export const GooglePlacesAutocomplete = forwardRef<
  HTMLInputElement,
  GooglePlacesAutocompleteProps
>(({ id, value, ...props }: GooglePlacesAutocompleteProps, ref) => {
  const { t, i18n } = useTranslation(["combined"]);
  const { apiHost = "", merchantSettings } = useChexContext();
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { setValue } = useFormContext();
  const logo = googleLogo as unknown as { src: string };
  const poweredBy: PoweredByType = {
    // eslint-disable-next-line
    text: t("checkoutForm.googleAutocomplete.text") as string,
    // eslint-disable-next-line
    brand: t("checkoutForm.googleAutocomplete.brand") as string,
    logo: {
      src: logo.src ? logo.src : (logo as unknown as string),
      width: 59,
      height: 18,
    },
  };
  const debounce = 300;
  const minLengthAutocomplete = 3;

  const [selectedValue, setSelectedValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<AutocompleteOptionType[]>([]);
  const { getPlaceDetails, getPlacePredictions } = usePlacesService({
    language: i18n.language,
    apiHost,
  });

  const validators = useMemo(() => {
    let validators = getValidatorsUpg();

    if (
      merchantSettings?.supportedPaymentGatewayType ===
      PaymentGatewayType.ORBITAL
    ) {
      validators = getValidatorsOrb();
    }

    return validators;
  }, []);

  useDebounce(
    () => {
      // Clean up suggestion (if we have old from previous search) if value length does not meet minimum length requirements
      // It covers case when we set value by store reducer
      if (value && value.length < minLengthAutocomplete) {
        setSuggestions([]);
      }

      // Logic to prevent requesting API when  value is selected from dropdown
      if (value && selectedValue !== value) {
        setDebouncedValue(value);
      } else {
        setSelectedValue("");
      }
    },
    debounce,
    [value]
  );

  const handleSelectOption = (prediction: AutocompleteOptionType) => {
    if (!prediction.prediction) return;

    getPlaceDetails({
      place_id: prediction.prediction.place_id,
    })
      .then((placeDetails) => {
        if (!placeDetails) return;

        const addressFields = mapAddressData(placeDetails);
        setSelectedValue(addressFields.billingAddressAddressLine1);

        for (const address in addressFields) {
          const validatedInputValue = runValidationRulesAutocomplete({
            validators,
            validatorId: getValidatorId(
              address,
              addressFields.billingAddressCountry
            ),
            newValue: addressFields[address],
            field: address,
          });
          setValue(`${paymentOption?.formId}.${address}`, validatedInputValue);
        }

        // Clean up suggestion as they are not representing new address that we prefilled based on place details request
        setSuggestions([]);
      })
      .catch((e) => {
        logger.error(e);
      });
  };

  useEffect(() => {
    if (minLengthAutocomplete > debouncedValue.length) return;

    setLoading(true);

    void getPlacePredictions({
      input: debouncedValue,
    })
      .then((data) => {
        setSuggestions(
          data.predictions.map((prediction) => ({
            value: prediction.place_id,
            title: prediction.structured_formatting.main_text,
            prediction,
          }))
        );
      })
      .catch((e) => logger.error(e))
      // converted from finally
      .then(() => {
        setLoading(false);
      });
  }, [debouncedValue]);

  return (
    <>
      <div id="google-map" />
      <DropdownAutocomplete
        {...props}
        ref={ref}
        id={id}
        value={value}
        loading={loading}
        options={suggestions}
        onSelectOption={handleSelectOption}
        poweredBy={poweredBy}
      />
    </>
  );
});

GooglePlacesAutocomplete.displayName = "GooglePlacesAutocomplete";
